/* global gdAlertify */
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import GdTable from "./GdTable";
import BuscarAuxiliar from "./BuscarAuxiliar";
import { formatDate } from "../../utils/utils";
import { ACTION_PERMISSIONS, hasActionPermission } from "../../utils/authPermissions";
import {
    DIAS_MAX_RETROACTIVO_ACUERDO,
    getFechaMinimaAcuerdo,
    validarFechaAcuerdo,
} from "../../utils/acuerdoMutuoFechas";

const normalizeMonto = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeFecha = (value) => value ? String(value).slice(0, 10) : "";

const getFechaLocalActual = () => {
    const ahora = new Date();
    ahora.setMinutes(ahora.getMinutes() - ahora.getTimezoneOffset());
    return ahora.toISOString().slice(0, 10);
};

const buscarAcuerdoPorContexto = (lista, contexto) => {
    if (!contexto?.idAcuerdo) return null;

    return lista.find((acuerdo) =>
        String(acuerdo.idAcuerdo) === String(contexto.idAcuerdo)
        && Number(acuerdo.estado) === 1
    ) || null;
};

const EstadoAcuerdoBadge = ({ estado }) => (
    estado === 1 ? (
        <span className="badge border border-success text-success bg-success bg-opacity-10 px-3 py-2">Activo</span>
    ) : (
        <span className="badge border border-danger text-danger bg-danger bg-opacity-10 px-3 py-2">Inactivo</span>
    )
);

const DetalleAcuerdo = ({ isOpen, onClose, details }) => {
    if (!isOpen || !details) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: "block", zIndex: 1065 }} tabIndex="-1">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-light border-bottom">
                            <h5 className="modal-title">Detalles del Acuerdo #{details.idAcuerdo}</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1"><strong>Paciente:</strong> {details.paciente}</p>
                                    <p className="mb-0"><strong>Auxiliar:</strong> {details.auxiliar}</p>
                                </div>
                                <EstadoAcuerdoBadge estado={details.estado} />
                            </div>

                            <div className="row mt-3">
                                <div className="col-md-6">
                                    <div className="p-3 border rounded bg-light">
                                        <p className="mb-1 text-muted">Fecha de Inicio</p>
                                        <p className="fw-bold mb-0">{formatDate(details.fechaInicio)}</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 border rounded bg-light">
                                        <p className="mb-1 text-muted">Fecha de Fin</p>
                                        <p className="fw-bold mb-0">{details.fechaFin ? formatDate(details.fechaFin) : "-"}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-md-4">
                                    <div className="p-3 border rounded bg-light text-center">
                                        <p className="text-muted mb-1">Valor Turno</p>
                                        <h6 className="fw-bold mb-0">${Number(details.valorTurno || 0).toLocaleString("es-CL")}</h6>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 border rounded bg-light text-center">
                                        <p className="text-muted mb-1">Movilizacion</p>
                                        <h6 className="fw-bold mb-0">${Number(details.valorMovilizacion || 0).toLocaleString("es-CL")}</h6>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 border rounded bg-light text-center">
                                        <p className="text-muted mb-1">Colacion</p>
                                        <h6 className="fw-bold mb-0">${Number(details.valorColacion || 0).toLocaleString("es-CL")}</h6>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4">
                                <h6 className="text-muted">Observacion</h6>
                                <div className="border rounded p-3 bg-light" style={{ minHeight: "70px" }}>
                                    {details.observacion?.trim()
                                        ? details.observacion
                                        : <span className="text-secondary fst-italic">Sin observaciones</span>}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
        </>
    );
};

const ModalCerrarAcuerdo = ({ isOpen, onClose, acuerdo, onSubmit, fechaInicial = "", turnoInicial = "" }) => {
    const [fecha, setFecha] = useState(() => new Date().toISOString().split("T")[0]);
    const [observacion, setObservacion] = useState("");
    const [turno, setTurno] = useState("noche");

    useEffect(() => {
        if (isOpen) {
            setFecha(fechaInicial || new Date().toISOString().split("T")[0]);
            setObservacion("");
            setTurno(turnoInicial || "noche");
        }
    }, [isOpen, acuerdo, fechaInicial, turnoInicial]);

    if (!isOpen || !acuerdo) return null;

    const errorFecha = validarFechaAcuerdo(fecha, "La fecha de cierre");

    const handleConfirmar = () => {
        if (errorFecha) {
            gdAlertify.notiError(errorFecha);
            return;
        }
        onSubmit({ idAcuerdo: acuerdo.idAcuerdo, fecha, observacion, turno });
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block", zIndex: 1065 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Cerrar Acuerdo</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <p>Deseas cerrar el acuerdo #{acuerdo.idAcuerdo}?</p>
                            <div className="mb-3">
                                <label className="form-label">Fecha de cierre</label>
                                <input
                                    type="date"
                                    className={`form-control ${errorFecha ? "is-invalid" : ""}`}
                                    value={fecha}
                                    min={getFechaMinimaAcuerdo()}
                                    disabled={Boolean(fechaInicial)}
                                    onChange={(e) => setFecha(e.target.value)}
                                />
                                {errorFecha
                                    ? <div className="invalid-feedback d-block">{errorFecha}</div>
                                    : <div className="form-text">
                                        Máximo {DIAS_MAX_RETROACTIVO_ACUERDO} días hacia atrás (desde {getFechaMinimaAcuerdo()}).
                                    </div>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Periodo</label>
                                <select className="form-select" value={turno} onChange={(e) => setTurno(e.target.value)}>
                                    <option value="dia">Dia</option>
                                    <option value="noche">Noche</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Observacion</label>
                                <textarea className="form-control" value={observacion} onChange={(e) => setObservacion(e.target.value)} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                            <button
                                className="btn btn-danger"
                                disabled={Boolean(errorFecha)}
                                title={errorFecha || ""}
                                onClick={handleConfirmar}
                            >
                                Confirmar cierre
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
        </>
    );
};

const ModalActualizarAcuerdo = ({ isOpen, onClose, acuerdo, onSubmit, fechaInicial = "" }) => {
    const puedeEditarValoresTurno = hasActionPermission(ACTION_PERMISSIONS.EDITAR_ACUERDO_VALOR_TURNO);
    const [fechaInicio, setFechaInicio] = useState("");
    const [auxiliar, setAuxiliar] = useState(null);
    const [turno, setTurno] = useState("");
    const [movilizacion, setMovilizacion] = useState("");
    const [colacion, setColacion] = useState("");
    const [tipoColacion, setTipoColacion] = useState("3");
    const [observacion, setObservacion] = useState("");

    useEffect(() => {
        if (acuerdo) {
            setFechaInicio(normalizeFecha(fechaInicial) || getFechaLocalActual());
            setTurno(normalizeMonto(acuerdo.valorTurno));
            setMovilizacion(normalizeMonto(acuerdo.valorMovilizacion));
            setColacion(normalizeMonto(acuerdo.valorColacion));
            setTipoColacion(acuerdo.tipo_colacion || "3");
            setObservacion(acuerdo.observacion || "");
            setAuxiliar(null);
        }
    }, [acuerdo, fechaInicial]);

    if (!isOpen || !acuerdo) return null;

    // El límite de 30 días aplica solo a la fecha NUEVA que se está fijando,
    // no a la fecha de inicio del acuerdo vigente.
    const errorLimiteFecha = validarFechaAcuerdo(fechaInicio, "La fecha de inicio del nuevo acuerdo");
    // El input no puede ir antes del inicio del acuerdo actual ni antes del límite de 30 días.
    const fechaMinimaInput = [normalizeFecha(acuerdo.fechaInicio), getFechaMinimaAcuerdo()]
        .filter(Boolean)
        .sort()
        .pop();

    const handleGuardar = () => {
        const idAuxiliarFinal = auxiliar?.id || acuerdo.idAuxiliar;
        if (!idAuxiliarFinal) {
            gdAlertify.notiError("No se pudo determinar el auxiliar.");
            return;
        }

        if (!fechaInicio) {
            gdAlertify.notiError("Selecciona la fecha de inicio del nuevo acuerdo.");
            return;
        }

        const errorLimite = validarFechaAcuerdo(fechaInicio, "La fecha de inicio del nuevo acuerdo");
        if (errorLimite) {
            gdAlertify.notiError(errorLimite);
            return;
        }

        const fechaAcuerdoActual = normalizeFecha(acuerdo.fechaInicio);
        if (fechaAcuerdoActual && fechaInicio < fechaAcuerdoActual) {
            gdAlertify.notiError("La fecha del nuevo acuerdo no puede ser anterior al inicio del acuerdo actual.");
            return;
        }

        const guardarActualizacion = () => onSubmit({
            idAcuerdo: acuerdo.idAcuerdo,
            fechaInicio,
            auxiliar: idAuxiliarFinal,
            turno: normalizeMonto(turno),
            movilizacion: normalizeMonto(movilizacion),
            colacion: normalizeMonto(colacion),
            tipo_colacion: tipoColacion,
            observacion: observacion.trim(),
        });

        if (fechaAcuerdoActual && fechaInicio === fechaAcuerdoActual) {
            gdAlertify.confirm({
                title: "Confirmar actualización desde el inicio",
                message: "La fecha del nuevo acuerdo es igual a la fecha de inicio del acuerdo actual. Los cambios se aplicarán desde el comienzo del acuerdo. ¿Desea continuar?",
                onConfirm: guardarActualizacion,
            });
            return;
        }

        guardarActualizacion();
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block", zIndex: 1065 }} tabIndex="-1">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Actualizar Acuerdo #{acuerdo.idAcuerdo}</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-2">
                                <p><strong>Paciente:</strong> {acuerdo.paciente}</p>
                                <p><strong>Auxiliar actual:</strong> {acuerdo.auxiliar}</p>
                                <p><strong>Estado:</strong> <EstadoAcuerdoBadge estado={acuerdo.estado} /></p>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">Fecha de inicio del acuerdo actual</label>
                                    <input type="date" className="form-control" value={normalizeFecha(acuerdo.fechaInicio)} disabled />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Fecha de inicio del nuevo acuerdo</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errorLimiteFecha ? "is-invalid" : ""}`}
                                        value={fechaInicio}
                                        min={fechaMinimaInput}
                                        disabled={Boolean(fechaInicial)}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                    />
                                    {errorLimiteFecha && (
                                        <div className="invalid-feedback d-block">{errorLimiteFecha}</div>
                                    )}
                                </div>
                                <div className="col-12 mt-2">
                                    <div className="form-text">Los cambios se aplicarán a los turnos desde la fecha del nuevo acuerdo.</div>
                                    <div className="form-text">
                                        Máximo {DIAS_MAX_RETROACTIVO_ACUERDO} días hacia atrás (desde {getFechaMinimaAcuerdo()}).
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Cambiar Auxiliar (opcional)</label>
                                <BuscarAuxiliar onSelect={setAuxiliar} />
                                <div className="mt-2 text-muted">
                                    {auxiliar
                                        ? <>Nuevo auxiliar: <strong>{auxiliar.nombre}</strong></>
                                        : <>Se mantendra el auxiliar actual: <strong>{acuerdo.auxiliar}</strong></>}
                                </div>
                            </div>

                            <div className="row">
                                {!puedeEditarValoresTurno && (
                                    <div className="col-12">
                                        <div className="alert alert-warning py-2 mb-2 small">
                                            Valor del turno en solo lectura para su perfil.
                                        </div>
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Valor Turno</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={turno}
                                            readOnly={!puedeEditarValoresTurno}
                                            onChange={(e) => setTurno(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Movilizacion</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={movilizacion}
                                            onChange={(e) => setMovilizacion(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="mb-3">
                                        <label className="form-label">Colacion</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={colacion}
                                            onChange={(e) => setColacion(e.target.value)}
                                        />
                                        <label className="form-label mt-2 small text-muted">Aplica en</label>
                                        <select
                                            className="form-select"
                                            value={tipoColacion}
                                            onChange={(e) => setTipoColacion(e.target.value)}
                                        >
                                            <option value="1">Dia</option>
                                            <option value="2">Noche</option>
                                            <option value="3">Ambos</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Observacion</label>
                                <textarea className="form-control" rows="2" value={observacion} onChange={(e) => setObservacion(e.target.value)}></textarea>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                            <button
                                className="btn btn-primary"
                                disabled={Boolean(errorLimiteFecha)}
                                title={errorLimiteFecha || ""}
                                onClick={handleGuardar}
                            >
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
        </>
    );
};

const ModalAcuerdosMutuos = ({ isOpen, onClose, accionInicial = "", contextoInicial = null }) => {
    const [acuerdos, setAcuerdos] = useState([]);
    const [filtroEstado, setFiltroEstado] = useState("todos");
    const [detalleAcuerdo, setDetalleAcuerdo] = useState(null);
    const [acuerdoCerrar, setAcuerdoCerrar] = useState(null);
    const [acuerdoActualizar, setAcuerdoActualizar] = useState(null);
    const [loading, setLoading] = useState(false);
    const esAccionDirecta = Boolean(accionInicial && contextoInicial);

    const fetchAcuerdos = async () => {
        setLoading(true);
        try {
            const response = contextoInicial?.idAcuerdo
                ? await axiosInstance.get(`/acuerdo-mutuo/detalle/${contextoInicial.idAcuerdo}`)
                : await axiosInstance.get("/acuerdo-mutuo/lista");
            const data = response.data?.data;
            const lista = Array.isArray(data) ? data : (data ? [data] : []);
            setAcuerdos(lista);
            return lista;
        } catch (error) {
            console.error("Error al buscar acuerdos: ", error);
            gdAlertify.notiError("No se pudo cargar la lista de acuerdos.");
            return [];
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        const cargar = async () => {
            const lista = await fetchAcuerdos();
            if (!accionInicial || !contextoInicial) return;

            const acuerdo = buscarAcuerdoPorContexto(lista, contextoInicial);
            if (!acuerdo) {
                gdAlertify.notiError(`No se encontró activo el acuerdo mutuo #${contextoInicial.idAcuerdo}.`);
                onClose();
                return;
            }

            setFiltroEstado("activos");
            if (accionInicial === "actualizar") setAcuerdoActualizar(acuerdo);
            if (accionInicial === "cerrar") setAcuerdoCerrar(acuerdo);
        };

        cargar();
    }, [isOpen, accionInicial, contextoInicial]);

    const acuerdosFiltrados = useMemo(() => {
        return acuerdos.filter((row) => {
            if (filtroEstado === "activos") return row.estado === 1;
            if (filtroEstado === "cerrados") return row.estado !== 1;
            return true;
        });
    }, [acuerdos, filtroEstado]);

    const abrirNuevoAcuerdo = () => {
        window.open("/acuerdo-mutuo", "_blank", "noopener,noreferrer");
    };

    const cerrarAcuerdo = async ({ idAcuerdo, fecha, observacion, turno }) => {
        setLoading(true);
        try {
            await axiosInstance.post("/acuerdo-mutuo/cerrar", { idAcuerdo, fecha, observacion, turno });
            gdAlertify.notiSuccess("Acuerdo cerrado correctamente.");
            setAcuerdoCerrar(null);
            if (esAccionDirecta) {
                onClose();
                return;
            }
            await fetchAcuerdos();
        } catch (error) {
            console.error("Error al cerrar acuerdo: ", error);
            gdAlertify.notiError("No se pudo cerrar el acuerdo.");
        } finally {
            setLoading(false);
        }
    };

    const actualizarAcuerdo = async (data) => {
        setLoading(true);
        try {
            await axiosInstance.post("/acuerdo-mutuo/actualizar", data);
            gdAlertify.notiSuccess("Acuerdo actualizado correctamente.");
            setAcuerdoActualizar(null);
            if (esAccionDirecta) {
                onClose();
                return;
            }
            await fetchAcuerdos();
        } catch (error) {
            console.error("Error al actualizar acuerdo: ", error);
            gdAlertify.notiError("No se pudo actualizar el acuerdo.");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { name: "idAcuerdo", title: "Id", width: 80 },
        { name: "paciente", title: "Paciente", width: 200 },
        { name: "auxiliar", title: "Auxiliar", width: 200 },
        { name: "fechaInicio", title: "Fecha Inicio", render: (row) => formatDate(row.fechaInicio), width: 120 },
        { name: "fechaFin", title: "Fecha Fin", render: (row) => row.fechaFin ? formatDate(row.fechaFin) : "-", width: 120 },
        { name: "observacion", title: "Observacion", width: 180 },
        { name: "estado", title: "Estado", render: (row) => <EstadoAcuerdoBadge estado={row.estado} />, width: 120 },
        {
            name: "acciones",
            title: "Acciones",
            render: (row) => (
                <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm" onClick={() => setDetalleAcuerdo(row)}>Ver</button>
                    {row.estado === 1 && (
                        <>
                            <button className="btn btn-success btn-sm" onClick={() => setAcuerdoActualizar(row)}>Actualizar</button>
                            <button className="btn btn-danger btn-sm" onClick={() => setAcuerdoCerrar(row)}>Cerrar</button>
                        </>
                    )}
                </div>
            ),
            width: 220,
        },
    ];

    if (!isOpen) return null;

    return (
        <>
            {!esAccionDirecta && (
            <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
                <div
                    className="modal-dialog modal-dialog-scrollable"
                    style={{ maxWidth: "90vw", width: "90vw", maxHeight: "90vh", margin: "5vh auto" }}
                >
                    <div className="modal-content position-relative" style={{ maxHeight: "90vh" }}>
                        {loading && (
                            <div
                                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                                style={{ zIndex: 20 }}
                            >
                                <div className="text-center">
                                    <div className="spinner-border text-primary mb-2" role="status"></div>
                                    <div className="fw-semibold text-primary">Cargando...</div>
                                </div>
                            </div>
                        )}
                        <div className="modal-header">
                            <h5 className="modal-title me-auto">
                                <i className="fas fa-file-contract me-2"></i>
                                Acuerdos Mutuos
                            </h5>
                            <button className="btn btn-success btn-sm me-2" onClick={abrirNuevoAcuerdo}>
                                <i className="fas fa-plus me-1"></i>
                                Nuevo Acuerdo
                            </button>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body" style={{ overflowY: "auto" }}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <span className="text-muted small fw-bold">Estado:</span>
                                <div className="btn-group btn-group-sm" role="group">
                                    <button
                                        type="button"
                                        className={`btn ${filtroEstado === "todos" ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => setFiltroEstado("todos")}
                                    >
                                        Ambos
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${filtroEstado === "activos" ? "btn-success" : "btn-outline-success"}`}
                                        onClick={() => setFiltroEstado("activos")}
                                    >
                                        Activos
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${filtroEstado === "cerrados" ? "btn-danger" : "btn-outline-danger"}`}
                                        onClick={() => setFiltroEstado("cerrados")}
                                    >
                                        Inactivos
                                    </button>
                                </div>
                            </div>
                            <GdTable
                                columns={columns}
                                data={acuerdosFiltrados}
                                defaultItemsPerPage={100}
                                itemsPerPageOptions={[10, 25, 50, 100]}
                                initialSort={[0, "desc"]}
                                stickyTop={0}
                            />
                        </div>
                    </div>
                </div>
            </div>
            )}

            {esAccionDirecta && loading && (
                <>
                    <div
                        className="modal d-block"
                        style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-body text-center py-4">
                                    <div className="spinner-border text-primary mb-3" role="status"></div>
                                    <div className="fw-semibold">Buscando acuerdo activo...</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}

            <DetalleAcuerdo
                isOpen={!!detalleAcuerdo}
                onClose={() => setDetalleAcuerdo(null)}
                details={detalleAcuerdo}
            />
            <ModalCerrarAcuerdo
                isOpen={!!acuerdoCerrar}
                onClose={() => {
                    setAcuerdoCerrar(null);
                    if (esAccionDirecta) onClose();
                }}
                acuerdo={acuerdoCerrar}
                onSubmit={cerrarAcuerdo}
                fechaInicial={contextoInicial?.fecha || ""}
                turnoInicial={String(contextoInicial?.turno) === "1" ? "dia" : "noche"}
            />
            <ModalActualizarAcuerdo
                isOpen={!!acuerdoActualizar}
                onClose={() => {
                    setAcuerdoActualizar(null);
                    if (esAccionDirecta) onClose();
                }}
                acuerdo={acuerdoActualizar}
                onSubmit={actualizarAcuerdo}
                fechaInicial={contextoInicial?.fecha || ""}
            />
        </>
    );
};

export default ModalAcuerdosMutuos;
